import { useQueryNavigation } from '@/hooks';
import { Fault } from '@/types/models/faults.types';
import { useMemo } from 'react';

export type DrawerView = { type: 'LIST' } | { type: 'DETAIL'; itemId: string; itemName: string };

export const useItemsDrawer = (items: Fault[]) => {
  const { currentParams, navigateWithQuery, pathname } = useQueryNavigation();

  const drawer = currentParams.drawer;
  const itemId = currentParams.itemId;

  const itemsMap = useMemo(() => new Map(items.map((i) => [i.fault_id, i])), [items]);

  const view: DrawerView | null =
    drawer === 'detail' && itemId
      ? { type: 'DETAIL', itemId, itemName: itemsMap?.get(itemId)?.fault_type.name || '' }
      : drawer === 'list'
      ? { type: 'LIST' }
      : null;

  const isOpen = Boolean(view);

  // Show breadcrumb when there are multiple items (navigated via list view)
  // Don't show when there's only 1 item or when clicked directly from table
  const showBreadcrumb = items.length > 1 && isOpen;

  // ---------- actions ----------

  const openList = () => {
    navigateWithQuery(pathname, {
      replaceParams: { drawer: 'list' },
      addParams: {},
    });
  };

  const openDetail = (id: string) => {
    navigateWithQuery(pathname, {
      replaceParams: { drawer: 'detail', itemId: id },
      addParams: {},
    });
  };

  const openDrawer = (clickedItem?: Fault) => {
    if (clickedItem) {
      // Table row click: open detail for this item
      openDetail(clickedItem.fault_id);
    } else if (items.length === 1) {
      // Only one item available: open detail automatically
      openDetail(items[0].fault_id);
    } else {
      // Multi-item page: show list first
      openList();
    }
  };

  const closeDrawer = () => {
    navigateWithQuery(pathname, {
      replaceParams: { drawer: null, itemId: null },
      addParams: {},
    });
  };

  return {
    isOpen,
    view,
    openDrawer,
    openList,
    openDetail,
    closeDrawer,
    showBreadcrumb,
  };
};
